if (!customElements.get('aerowise-banner')) {
  customElements.define(
    'aerowise-banner',
    class AerowiseBanner extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        const options = { signal: this.abortController.signal };
        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.template = this.querySelector('.aerowise-banner__video-template');
        this.toggle = this.querySelector('.aerowise-banner__toggle');
        this.userPaused = false;
        this.inView = false;
        this.applySettings();

        this.mobileQuery.addEventListener('change', () => this.updateVideo(), options);
        this.motionQuery.addEventListener('change', () => this.updateVideo(), options);
        document.addEventListener('visibilitychange', () => this.updateVideo(), options);
        this.toggle?.addEventListener('click', () => {
          this.userPaused = !this.video.paused;
          this.updateVideo();
        }, options);

        if (this.template) {
          this.visibilityObserver = new IntersectionObserver((entries) => {
            this.inView = entries[0].isIntersecting;
            this.updateVideo();
          });
          this.visibilityObserver.observe(this);
        }

        this.updateHeader = this.updateHeader.bind(this);
        document.addEventListener('shopify:section:reorder', this.updateHeader, options);
        document.addEventListener('shopify:section:load', this.updateHeader, options);
        document.addEventListener('shopify:section:unload', this.updateHeader, options);
        this.sectionObserver = new MutationObserver(this.updateHeader);
        const main = document.getElementById('MainContent');
        if (main) this.sectionObserver.observe(main, { childList: true });
        this.headerFrame = requestAnimationFrame(this.updateHeader);
      }

      disconnectedCallback() {
        this.abortController.abort();
        this.visibilityObserver?.disconnect();
        this.sectionObserver.disconnect();
        cancelAnimationFrame(this.headerFrame);
        this.removeVideo();
        this.releaseHeader();
      }

      applySettings() {
        const properties = {
          '--aerowise-custom-top': `${this.dataset.paddingTop}px`,
          '--aerowise-custom-bottom': `${this.dataset.paddingBottom}px`,
          '--aerowise-custom-top-mobile': `${this.dataset.paddingTopMobile}px`,
          '--aerowise-custom-bottom-mobile': `${this.dataset.paddingBottomMobile}px`,
          '--aerowise-desktop-position': this.dataset.desktopPosition,
          '--aerowise-mobile-position': this.dataset.mobilePosition,
        };
        Object.entries(properties).forEach(([property, value]) => this.style.setProperty(property, value));
        this.querySelector('.aerowise-banner__image')?.style.removeProperty('object-position');
      }

      updateVideo() {
        if (!this.template) return;
        if ((this.mobileQuery.matches && this.dataset.mobileImage === 'true') || this.motionQuery.matches) {
          this.removeVideo();
          return;
        }
        if (!this.video && this.inView && !document.hidden) {
          this.video = this.template.content.querySelector('video').cloneNode(true);
          this.video.muted = true;
          this.video.playsInline = true;
          this.video.setAttribute('aria-hidden', 'true');
          this.video.addEventListener('playing', () => {
            this.setAttribute('data-video-ready', '');
            this.updateToggle();
          });
          this.video.addEventListener('pause', () => this.updateToggle());
          this.video.addEventListener('error', () => {
            this.removeAttribute('data-video-ready');
            this.toggle.hidden = true;
          });
          this.querySelector('.aerowise-banner__media').append(this.video);
          this.toggle.hidden = false;
        }
        if (!this.video) return;
        if (this.userPaused || !this.inView || document.hidden) {
          this.video.pause();
        } else {
          this.video.play().catch(() => this.updateToggle());
        }
        this.updateToggle();
      }

      updateToggle() {
        if (!this.video || !this.toggle) return;
        this.toggle.toggleAttribute('data-paused', this.video.paused);
        this.toggle.setAttribute('aria-label', this.video.paused ? this.toggle.dataset.playLabel : this.toggle.dataset.pauseLabel);
      }

      removeVideo() {
        if (this.video) {
          this.video.pause();
          this.video.removeAttribute('src');
          this.video.querySelectorAll('source').forEach((source) => source.remove());
          this.video.load();
          this.video.remove();
          this.video = null;
        }
        this.removeAttribute('data-video-ready');
        if (this.toggle) this.toggle.hidden = true;
      }

      updateHeader() {
        const firstSection = document.querySelector('#MainContent > .shopify-section');
        const headerWrapper = document.querySelector('[data-aerowise-banner-header]');
        if (this.dataset.transparentHeader !== 'true' || !firstSection?.contains(this) || !headerWrapper) {
          this.releaseHeader();
          return;
        }
        const header = headerWrapper.closest('.section-header');
        if (!header) return;
        if (this.header !== header) {
          this.releaseHeader();
          this.header = header;
          this.headerResizeObserver = new ResizeObserver(() => this.measureHeader());
          this.headerResizeObserver.observe(headerWrapper);
        }
        header.dataset.aerowiseBannerOwner = this.id;
        header.dataset.aerowiseBannerStyle = this.dataset.transparentStyle;
        this.measureHeader();
        headerWrapper.setHeaderHeight?.();
        headerWrapper.createObserver?.();
      }

      measureHeader() {
        if (!this.header || this.header.dataset.aerowiseBannerOwner !== this.id) return;
        const height = this.header.querySelector('[data-aerowise-banner-header]').offsetHeight;
        this.header.style.setProperty('--aerowise-header-height', `${height}px`);
        this.style.setProperty('--aerowise-header-offset', `${height}px`);
      }

      releaseHeader() {
        this.headerResizeObserver?.disconnect();
        if (this.header?.dataset.aerowiseBannerOwner === this.id) {
          delete this.header.dataset.aerowiseBannerOwner;
          delete this.header.dataset.aerowiseBannerStyle;
          this.header.style.removeProperty('--aerowise-header-height');
          const headerWrapper = this.header.querySelector('[data-aerowise-banner-header]');
          headerWrapper?.setHeaderHeight?.();
          headerWrapper?.createObserver?.();
        }
        this.style.removeProperty('--aerowise-header-offset');
        this.header = null;
      }
    }
  );
}
